// ABOUTME: Utility functions for rendering subscription status banners with color coding and context
// ABOUTME: Handles 8 subscription states: trial, active, cancelled, expired, paused, refunded, past_due, free

import { SubscriptionInfo } from './types';

export interface BannerConfig {
  emoji: string;
  color: 'success' | 'warning' | 'error' | 'info';
  title: string;
  subtitle?: string;
  context?: string[];
}

export function getSubscriptionBannerConfig(subscriptionInfo: SubscriptionInfo | null): BannerConfig {
  if (!subscriptionInfo) {
    return {
      emoji: '⚪',
      color: 'info',
      title: 'No subscription information available',
    };
  }

  const status = subscriptionInfo.status.toLowerCase();

  // Lockout takes highest priority
  if (subscriptionInfo.isLockedOut) {
    return {
      emoji: '🔒',
      color: 'error',
      title: `LOCKED OUT - ${subscriptionInfo.lockedOutReason || 'No reason provided'}`,
      context: [
        subscriptionInfo.lockedOutAt ? `Locked on: ${formatDate(subscriptionInfo.lockedOutAt)}` : '',
        subscriptionInfo.lockedOutByName ? `By: ${subscriptionInfo.lockedOutByName}` : '',
      ].filter(Boolean),
    };
  }

  // Free accounts take priority over status-based display
  if (subscriptionInfo.isFreeAccount) {
    return {
      emoji: '🔵',
      color: 'info',
      title: `FREE ACCOUNT - Access Until ${formatDate(subscriptionInfo.freeAccountExpiresAt)}`,
    };
  }

  switch (status) {
    case 'trial':
      return {
        emoji: '🟡',
        color: 'warning',
        title: `TRIAL - Access Until ${formatDate(subscriptionInfo.trialEndsAt)}`,
      };

    case 'active':
      if (subscriptionInfo.hasAutoRenew) {
        return {
          emoji: '🟢',
          color: 'success',
          title: `ACTIVE - Access Until ${formatDate(subscriptionInfo.currentPeriodEnd)}`,
        };
      } else {
        // Active but auto-renew off (shouldn't happen - should be "cancelled" status)
        return {
          emoji: '🟠',
          color: 'warning',
          title: `ACTIVE - Access Until ${formatDate(subscriptionInfo.currentPeriodEnd)}`,
        };
      }

    case 'cancelled':
      return {
        emoji: '🟠',
        color: 'warning',
        title: `CANCELLED - Access Until ${formatDate(subscriptionInfo.currentPeriodEnd)}`,
        context: [
          `Was cancelled on: ${formatDate(subscriptionInfo.cancelledAt)}`,
          `Subscription ran: ${formatDate(subscriptionInfo.currentPeriodStart)} - ${formatDate(subscriptionInfo.currentPeriodEnd)}`,
        ],
      };

    case 'expired': {
      const expirationDate = subscriptionInfo.currentPeriodEnd || subscriptionInfo.trialEndsAt;
      const cancelledContext = subscriptionInfo.cancelledAt
        ? [`Was cancelled on: ${formatDate(subscriptionInfo.cancelledAt)}`]
        : [];

      return {
        emoji: '🔴',
        color: 'error',
        title: `NO ACCESS - Expired on ${formatDate(expirationDate)}`,
        context: [
          ...cancelledContext,
          `Subscription ran: ${formatDate(subscriptionInfo.currentPeriodStart || subscriptionInfo.createdAt)} - ${formatDate(expirationDate)}`,
        ],
      };
    }

    case 'refunded':
      return {
        emoji: '🔴',
        color: 'error',
        title: `NO ACCESS - Refunded on ${formatDate(subscriptionInfo.cancelledAt || subscriptionInfo.createdAt)}`,
        context: [
          `Originally purchased: ${formatDate(subscriptionInfo.createdAt)}`,
          `Original period was: ${formatDate(subscriptionInfo.currentPeriodStart)} - ${formatDate(subscriptionInfo.currentPeriodEnd)}`,
        ],
      };

    case 'paused':
      return {
        emoji: '🔴',
        color: 'error',
        title: `NO ACCESS - Paused on ${formatDate(subscriptionInfo.cancelledAt)}`,
        context: [
          `Was active since: ${formatDate(subscriptionInfo.createdAt)}`,
          'Had access until paused',
        ],
      };

    case 'past_due':
      return {
        emoji: '🟣',
        color: 'warning',
        title: `PAST DUE - Payment Failed, Retrying`,
        subtitle: 'Access temporarily maintained',
        context: [
          `Access will be removed on: ${formatDate(subscriptionInfo.currentPeriodEnd)}`,
        ],
      };

    case 'free':
      return {
        emoji: '🔵',
        color: 'info',
        title: 'FREE ACCOUNT - Unlimited Access',
      };

    default:
      return {
        emoji: '⚪',
        color: 'info',
        title: `Unknown Status: ${subscriptionInfo.status}`,
      };
  }
}

function formatDate(dateString: string | undefined): string {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return 'Invalid Date';
  }
}
